import React, { useState } from 'react';
import { 
  FaFileContract, FaCheckCircle, FaChevronDown, FaBalanceScale, FaShieldAlt 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What services does Lotoswealth provide under these Terms?",
      a: "Lotoswealth provides comprehensive financial advisory and wealth management solutions, including portfolio management services (PMS), financial planning, goal-based investments, tax planning, retirement and estate planning, alternative investments, private equity, private credit, asset management, mutual funds, stock investments, and loans."
    },
    {
      q: "Are the investment recommendations guaranteed?",
      a: "No. Investments in securities, mutual funds, and financial products are subject to market risks. Past performance does not guarantee future results, and Lotoswealth does not guarantee returns or profits on any investment product."
    },
    {
      q: "What are my responsibilities as a user?",
      a: "You must be at least 18 years old, legally capable of entering into binding contracts, provide accurate information, keep your login credentials secure, use the website only for lawful purposes, and avoid uploading malicious software or interfering with platform systems."
    },
    {
      q: "How are loan approvals and third-party products handled?",
      a: "Loan approvals, interest rates, and eligibility are determined solely by respective lending institutions. Lotoswealth acts only as an advisory and facilitation partner and does not guarantee loan approval or take responsibility for external third-party products."
    }
  ];

  return (
    <main className="terms-of-service-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Terms of Use | Legal Agreements & Usage Conditions | Lotoswealth</title>

        <meta
          name="description"
          content="Review the complete Terms of Use for Lotoswealth. Understand the legal conditions, user eligibility, investment risk disclosures, intellectual property rights, and liability guidelines."
        />

        <meta
          name="keywords"
          content="Terms of Use, Lotoswealth Terms, User Agreement, Legal Conditions, Financial Advisory Terms, Investment Risk Disclosure, Wealth Management Terms, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/terms-of-service"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/terms-of-service"
        />
        <meta
          property="og:title"
          content="Terms of Use | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Read the comprehensive terms and conditions governing the use of Lotoswealth's digital platforms, wealth management, and financial advisory services."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Terms of Use | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Understand your rights, user obligations, investment disclaimers, and legal guidelines when accessing Lotoswealth's financial services."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>

      {/* =========================================
          1. HERO SECTION
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '60vh', minHeight: '450px', overflow: 'hidden', backgroundColor: '#0a192f' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 opacity-25">
          <img 
            src="/images/lotoswealth-bg.jpg" 
            alt="Terms of Use Background" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Terms of Use
          </h1>
          <p className="hero-description animate-slide-up delay-2 text-white mx-auto" style={{ maxWidth: '800px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Last Updated: 06 August 2026. Welcome to Lotoswealth. Please read these terms carefully before accessing or using our website, digital platforms, and financial services.
          </p>
        </div>
      </div>

      {/* =========================================
          2. CORE AGREEMENT OVERVIEW
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Binding Agreement Between You and Lotoswealth
              </h2>
              <p className="text-muted line-height-lg">
                These Terms of Use ("Terms") govern your access to and use of our website, digital platforms, products, and financial services ("Services"). By accessing or using our website, you agree to comply with these Terms. If you do not agree with any part of these Terms, please discontinue the use of our Services.
              </p>
              <p className="text-muted line-height-lg">
                These Terms constitute a legally binding agreement between Lotoswealth ("we," "our," or "us") and every visitor, client, or user ("you" or "your").
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Highlights</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Eligibility:</strong> Must be 18+ and legally capable of contracting.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Risk Disclosure:</strong> Market investments are subject to risk.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Intellectual Property:</strong> All content belongs exclusively to Lotoswealth.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Governing Law:</strong> Subject to the laws of India and courts in Hyderabad.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. DETAILED TERMS CLAUSES
      ========================================= */}
      <section className="py-5 bg-white">
        <div className="container py-4" style={{ maxWidth: '900px' }}>
          
          <div className="mb-5 animate-slide-up delay-1">
            <h3 className="fw-bold text-dark mb-3">1. Acceptance of Terms & Modifications</h3>
            <p className="text-muted">
              By accessing or using the Lotoswealth website or any related services, you acknowledge that you have read, understood, and agreed to these Terms, our Privacy Policy, and any additional policies published on our website. We reserve the right to modify these Terms at any time. Any changes will become effective immediately upon publication. Continued use of our website after such updates constitutes your acceptance of the revised Terms.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-2">
            <h3 className="fw-bold text-dark mb-3">2. Eligibility Requirements</h3>
            <p className="text-muted">To use our Services, you must meet the following criteria:</p>
            <ul className="text-muted">
              <li className="mb-2">Be at least 18 years of age.</li>
              <li className="mb-2">Be legally capable of entering into binding contracts under applicable laws.</li>
              <li className="mb-2">Provide accurate and complete information whenever requested.</li>
              <li className="mb-2">Comply with all applicable laws and regulations.</li>
            </ul>
          </div>

          <div className="mb-5 animate-slide-up delay-3">
            <h3 className="fw-bold text-dark mb-3">3. Our Services & Scope</h3>
            <p className="text-muted">
              Lotoswealth provides financial advisory and wealth management solutions, including but not limited to Wealth Management, Portfolio Management Services (PMS), Financial Planning, Goal-Based Investments, Tax Planning, Retirement Planning, Estate Planning, Alternative Investments, Private Equity, Private Credit, Asset Management, Mutual Funds, Stock Investments, Home Loans, Car Loans, and Education Loans. All services are provided in accordance with applicable regulations and internal policies.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-4">
            <h3 className="fw-bold text-dark mb-3">4. User Responsibilities & Conduct</h3>
            <p className="text-muted">When using our website or services, you agree to:</p>
            <ul className="text-muted">
              <li className="mb-2">Provide accurate and updated information.</li>
              <li className="mb-2">Keep your login credentials secure.</li>
              <li className="mb-2">Use the website only for lawful purposes.</li>
              <li className="mb-2">Avoid unauthorized access, misuse, or interference with our systems.</li>
              <li className="mb-2">Not upload harmful software, malware, or malicious code.</li>
            </ul>
            <p className="text-muted mt-2">
              You remain solely responsible for all activities conducted using your account.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-5">
            <h3 className="fw-bold text-dark mb-3">5. Investment Risk & Financial Advice Disclaimers</h3>
            <p className="text-muted">
              Investments in securities, mutual funds, and financial products are subject to market risks. Past performance does not guarantee future results. Investment values may rise or fall depending on market conditions. Lotoswealth does not guarantee returns or profits on any investment product or financial strategy. Clients should carefully evaluate their financial goals and risk appetite before making investment decisions.
            </p>
            <p className="text-muted mt-2">
              Information available on our website is intended for educational and informational purposes only. Nothing published on this website should be interpreted as legal, tax, accounting, or guaranteed investment advice. Personalized recommendations are provided only after evaluating an individual's financial profile and investment objectives.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-6">
            <h3 className="fw-bold text-dark mb-3">6. Third-Party Products, Services & Loans</h3>
            <p className="text-muted">
              Our platform may facilitate access to third-party financial institutions, lenders, insurers, or investment providers. Lotoswealth is not responsible for products or services offered by third parties, changes in interest rates, investment performance, processing delays, or service quality provided by external organizations. Users should independently review the terms and conditions of such third-party providers.
            </p>
            <p className="text-muted mt-2">
              Loan approvals, interest rates, processing timelines, and eligibility are determined solely by the respective lending institutions. Lotoswealth acts only as a financial advisory and facilitation partner and does not guarantee loan approval.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-7">
            <h3 className="fw-bold text-dark mb-3">7. Intellectual Property & Privacy</h3>
            <p className="text-muted">
              All website content, including text, graphics, logos, icons, images, designs, software, and branding, belongs to Lotoswealth unless otherwise stated. No content may be copied, reproduced, modified, distributed, or commercially exploited without prior written permission.
            </p>
            <p className="text-muted mt-2">
              Your use of our Services is also governed by our Privacy Policy. We collect, use, and protect your personal information in accordance with applicable privacy laws.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-8">
            <h3 className="fw-bold text-dark mb-3">8. Limitation of Liability & Indemnification</h3>
            <p className="text-muted">
              To the maximum extent permitted by law, Lotoswealth shall not be liable for investment losses, business interruptions, technical failures, website downtime, delayed transactions, third-party system failures, or indirect/consequential damages. Users access and use the Services at their own risk.
            </p>
            <p className="text-muted mt-2">
              You agree to indemnify and hold harmless Lotoswealth, its directors, employees, affiliates, and partners against any claims, damages, liabilities, losses, or expenses arising from your misuse of our Services or violation of these Terms.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-9">
            <h3 className="fw-bold text-dark mb-3">9. Suspension, Termination & Governing Law</h3>
            <p className="text-muted">
              We reserve the right to suspend or terminate access to our website or services without prior notice if these Terms are violated, fraudulent or suspicious activities are detected, required by law or regulatory authorities, or security concerns arise.
            </p>
            <p className="text-muted mt-2">
              These Terms shall be governed by and interpreted in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts located in Hyderabad, Telangana.
            </p>
          </div>

        </div>
      </section>

      {/* =========================================
          4. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding our Terms of Use</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          5. FINAL CTA SECTION
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Have Questions About Our Terms?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            If you need further clarification regarding our service agreements or policies, our team is always ready to assist you.
          </p>
          <a href="/contact-us" className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3 text-decoration-none d-inline-block">
            Contact Us Today
          </a>
        </div>
      </section>

    </main>
  );
};

export default TermsOfService;